import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockBatch, StockItemDetail } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Textarea } from "../../app/components/primitives/textarea";
import { batchLabel } from "./AdjustStockDialog";
import {
  FieldShell,
  type FieldErrorMap,
  toFieldErrorMap,
} from "./StockFormField";

interface WithdrawBatchDialogProps {
  item: StockItemDetail;
  batch: StockBatch;
  onClose: () => void;
  onWithdrawn: (detail: StockItemDetail) => void;
}

/** Withdraw a batch for good: what is left of it is written off and it takes no
 * further movement. The note says why and stays on the batch. */
export function WithdrawBatchDialog({
  item,
  batch,
  onClose,
  onWithdrawn,
}: WithdrawBatchDialogProps) {
  const dataAdapter = useDataAdapter();
  const [note, setNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const remaining = item.holdings
    .filter((h) => h.batch_id === batch.id)
    .reduce((sum, h) => sum + h.on_hand, 0);

  async function handleWithdraw() {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      onWithdrawn(
        await dataAdapter.withdrawStockBatch(item.id, batch.id, note),
      );
    } catch (error) {
      console.error("stock.batch.withdraw_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : "Couldn't withdraw the batch. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Withdraw batch</DialogTitle>
          <DialogDescription>
            {item.stock_code} ·{" "}
            {batchLabel(batch.batch_no, batch.artwork_revision_id)}.{" "}
            {remaining > 0
              ? `The ${formatCount(remaining)} left in the batch will be written off.`
              : "Nothing is left in the batch."}{" "}
            A withdrawn batch can't be booked into or out of again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}
          <FieldShell
            id="withdraw-batch-note"
            label="Why is the batch being withdrawn?"
            error={fieldErrors.note}
            hint="Recorded with your name on the batch and in the history."
          >
            {(aria) => (
              <Textarea
                id="withdraw-batch-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleWithdraw()}
            disabled={isSaving || note.trim() === ""}
          >
            {isSaving ? "Withdrawing…" : "Withdraw batch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
